// Dependencies
import React, { Component } from 'react';
import Highlighter from 'react-highlight-words';
import { connect } from 'react-redux';

import CheckBox from 'components/atoms/CheckBox';
import Input from 'components/atoms/Input';
import RangeSlider from 'components/atoms/RangeSlider';
import SearchGroup from 'components/atoms/SearchGroup';
import { bindActionCreators, Dispatch } from 'redux';
import { searchAutoComplate } from 'services/search';
import { SetErrorHandlerAction, setErrorHandlerAction } from 'store/actions/errorsActions';
import { SetSearchObjectAction } from 'store/actions/layoutActions';
import { DataDisplayType, PortType, RootState, VesselCategoryType } from 'types';
import { DefaultData, FilterObjectType } from 'types/search';
import { checkDifferent, clone, getKey, setKey } from 'utils/formHandlers';
import { formatVesselFoundList } from 'utils/migrations';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface VesselFiltersComponentProps {
    vesselCategories: VesselCategoryType[];
    defaultData: DefaultData;
    port: PortType;
    filterObject: FilterObjectType;
    setFilterObject: SetSearchObjectAction;
    setErrorHandlerAction: SetErrorHandlerAction;
}

interface VesselFiltersComponentState {
    language: string;
    port: PortType;
    vesselCategories: VesselCategoryType[];
    filterObject: FilterObjectType;
    dataDisplay: DataDisplayType | undefined;
    foundVessles: { id: number; value: string; type: string }[];
    foundtemsMenu: boolean;
}

class VesselFilters extends Component<VesselFiltersComponentProps, VesselFiltersComponentState> {
    vesselFilterItems = React.createRef<HTMLDivElement>();
    clickOutsideMenuEvent: any = null;
    constructor(props: VesselFiltersComponentProps) {
        super(props);
        this.state = {
            port: props.port,
            dataDisplay: props.port.meta_data.dataDisplay,
            vesselCategories: props.vesselCategories,
            language: 'en',
            filterObject: props.filterObject,
            foundVessles: [],
            foundtemsMenu: false,
        };
    }

    static getDerivedStateFromProps(props: VesselFiltersComponentProps, state: VesselFiltersComponentState) {
        if (checkDifferent(props.filterObject, state.filterObject)) {
            return {
                filterObject: props.filterObject,
            };
        }

        return null;
    }

    componentDidMount() {
        this.clickOutsideMenuEvent = document.addEventListener('mouseup', this.handleClickOutsideMenu);
    }

    componentWillUnmount() {
        document.removeEventListener('mouseup', this.clickOutsideMenuEvent);
    }

    handleClickInsideMenu = () => this.setState({ foundtemsMenu: !this.state.foundtemsMenu });

    handleClickOutsideMenu = (e) => {
        if (this.vesselFilterItems.current != null && !this.vesselFilterItems.current.contains(e.target)) {
            this.setState({ foundtemsMenu: false });
        }
    };

    handleChange = (value: number) => {
        const filterObject = clone(this.state.filterObject);
        const vesselCategories = [...filterObject.vesselCategories];

        const updatedCategories = vesselCategories.includes(value)
            ? vesselCategories.filter((category) => category !== value) // Remove value
            : [...vesselCategories, value]; // Add value

        const updatedFilterObject = { ...filterObject, vesselCategories: updatedCategories };

        this.props.setFilterObject(updatedFilterObject);
    };

    handleVessleNameChange = (name: string, value: string, search = true) => {
        const filterObject = clone(this.state.filterObject); // Clone the filterObject
        if (value.length >= 3 && search) {
            this.autoComplate(value);
        } else {
            this.setState({ foundtemsMenu: false });
        }
        setKey(filterObject, name, value);
        this.props.setFilterObject(filterObject);
    };

    handleSliderChange = (names: string[], values: number[]): void => {
        const filterObject = clone(this.state.filterObject); // Clone the filterObject

        names.forEach((name, index) => {
            const value = values[index];
            const defaultValue = getKey(this.props.defaultData, name);

            // Set the current key's value
            setKey(filterObject, name, value);

            // Handle "min" and "max" flags for "gte" and "lte"
            if (name.includes('gte')) {
                const minKey = name.replace('gte', 'min');
                setKey(filterObject, minKey, value === defaultValue);
            } else if (name.includes('lte')) {
                const maxKey = name.replace('lte', 'max');
                setKey(filterObject, maxKey, value === defaultValue);
            }
        });

        this.props.setFilterObject(filterObject); // Update the filterObject
    };

    getDisplay = (type: string, key: string) => {
        const dataDisplay = this.state?.dataDisplay;

        if (dataDisplay?.[type]?.[key]) {
            return dataDisplay[type][key];
        }

        return {};
    };

    autoComplate = (value: string) => {
        searchAutoComplate(this.props.port.id, this.props.port.backEnd, { find: value })
            .then((res) => {
                const isNumber = !isNaN(Number(value));
                const items = formatVesselFoundList(res, this.props.port.backEnd?.version || 1);

                if (isNumber && items.length === 1) {
                    this.handleVessleNameChange('vessel.name.icontains', items[0].value, true);
                } else {
                    this.setState({
                        foundVessles: items,
                        foundtemsMenu: true,
                    });
                }
            })
            .catch((error) => {
                this.props.setErrorHandlerAction(error);
            });
    };

    render() {
        return (
            <SearchGroup open title={translate('Vessel')}>
                <div className="flex flex-col gap-x-1">
                    <div className="flex flex-col gap-x-1 relative">
                        <Input
                            value={getKey(this.state.filterObject, 'vessel.name.icontains')}
                            name="vessel.name.icontains"
                            placeholder={translate('Vessel name')}
                            className={twclsx(
                                'h-6',
                                'mt-2',
                                'mr-2',
                                'mb-2',
                                'w-auto',
                                this.state.foundtemsMenu && 'rounded-b-none'
                            )}
                            onChange={this.handleVessleNameChange}
                        />
                        {this.state.foundtemsMenu && (
                            <div
                                onClick={this.handleClickInsideMenu}
                                ref={this.vesselFilterItems}
                                className=" absolute mt-8 pr-2 w-full z-10 "
                            >
                                <div
                                    className="overflow-y-auto h-auto scrollable overflow-x-hidden overflow-scroll border-l border-r border-b border-gray-300 bg-gray-100"
                                    style={{ maxHeight: 150, minHeight: 30 }}
                                >
                                    {this.state.foundVessles.length === 0 && (
                                        <div className="px-2   py-1 h-6">{translate('No results')}</div>
                                    )}
                                    <>
                                        {this.state.foundVessles.map((x) => {
                                            return (
                                                <div
                                                    className="px-2 cursor-pointer py-1 h-6"
                                                    onClick={() => {
                                                        this.handleVessleNameChange(
                                                            'vessel.name.icontains',
                                                            x.value,
                                                            false
                                                        );
                                                    }}
                                                    key={x.id}
                                                >
                                                    <Highlighter
                                                        highlightClassName="text-blue-500"
                                                        searchWords={[
                                                            getKey(this.state.filterObject, 'vessel.name.icontains'),
                                                        ]}
                                                        autoEscape={true}
                                                        textToHighlight={x.value}
                                                    ></Highlighter>
                                                </div>
                                            );
                                        })}
                                    </>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-x-1">
                        {this.state.vesselCategories
                            .filter((vesselCategory: VesselCategoryType) => {
                                return (
                                    this.props.defaultData.vesselCategories.includes(vesselCategory.id) ||
                                    this.props.defaultData.vesselCategories.includes(vesselCategory.name)
                                );
                            })
                            .map((vesselCategory: VesselCategoryType) => {
                                return (
                                    <div key={vesselCategory.id} className="flex gap-1 cursor-pointer">
                                        <div>
                                            <CheckBox
                                                name={vesselCategory.name}
                                                onChange={() => {
                                                    this.handleChange(vesselCategory.id);
                                                }}
                                                value={this.state.filterObject?.vesselCategories.includes(
                                                    vesselCategory.id
                                                )}
                                            ></CheckBox>
                                        </div>
                                        <div
                                            onClick={() => {
                                                this.handleChange(vesselCategory.id);
                                            }}
                                        >
                                            {vesselCategory.label}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                    <div className="pl-1">
                        <RangeSlider
                            onChange={(name, values) => {
                                this.handleSliderChange(
                                    name,
                                    values.map((x) => {
                                        return x / this.getDisplay('vessel', 'loa_m').database2display_unit_factor;
                                    })
                                );
                            }}
                            key={`vessel.loa_m`}
                            name={[`vessel.loa_m.gte`, `vessel.loa_m.lte`]}
                            unit={this.getDisplay('vessel', 'loa_m').unit}
                            label={this.getDisplay('vessel', 'loa_m').name}
                            decimal={this.getDisplay('vessel', 'loa_m').decimal}
                            value={[
                                getKey(this.state.filterObject, `vessel.loa_m.gte`) *
                                    this.getDisplay('vessel', 'loa_m').database2display_unit_factor,
                                getKey(this.state.filterObject, `vessel.loa_m.lte`) *
                                    this.getDisplay('vessel', 'loa_m').database2display_unit_factor,
                            ]}
                            range={[
                                getKey(this.props.defaultData, `vessel.loa_m.gte`) *
                                    this.getDisplay('vessel', 'loa_m').database2display_unit_factor,
                                getKey(this.props.defaultData, `vessel.loa_m.lte`) *
                                    this.getDisplay('vessel', 'loa_m').database2display_unit_factor,
                            ]}
                        ></RangeSlider>
                    </div>
                    <div className="pl-1">
                        <RangeSlider
                            onChange={(name, values) => {
                                this.handleSliderChange(
                                    name,
                                    values.map((x) => {
                                        return x * this.getDisplay('vessel', 'beam_m').api2database_unit_factor;
                                    })
                                );
                            }}
                            key={`vessel.beam_m`}
                            name={[`vessel.beam_m.gte`, `vessel.beam_m.lte`]}
                            unit={this.getDisplay('vessel', 'beam_m').unit}
                            label={this.getDisplay('vessel', 'beam_m').name}
                            decimal={this.getDisplay('vessel', 'beam_m').decimal}
                            value={[
                                getKey(this.state.filterObject, `vessel.beam_m.gte`) *
                                    this.getDisplay('vessel', 'beam_m').database2display_unit_factor,
                                getKey(this.state.filterObject, `vessel.beam_m.lte`) *
                                    this.getDisplay('vessel', 'beam_m').database2display_unit_factor,
                            ]}
                            range={[
                                getKey(this.props.defaultData, `vessel.beam_m.gte`) *
                                    this.getDisplay('vessel', 'beam_m').database2display_unit_factor,
                                getKey(this.props.defaultData, `vessel.beam_m.lte`) *
                                    this.getDisplay('vessel', 'beam_m').database2display_unit_factor,
                            ]}
                        ></RangeSlider>
                    </div>
                    <div className="pl-1">
                        <RangeSlider
                            onChange={(name, values) => {
                                this.handleSliderChange(
                                    name,
                                    values.map((x) => {
                                        return x * this.getDisplay('vessel', 'draft_m').api2database_unit_factor;
                                    })
                                );
                            }}
                            key={`transits.draft_m`}
                            name={[`transits.draft_m.gte`, `transits.draft_m.lte`]}
                            unit={this.getDisplay('vessel', 'draft_m').unit}
                            label={this.getDisplay('vessel', 'draft_m').name}
                            decimal={this.getDisplay('vessel', 'draft_m').decimal}
                            value={[
                                getKey(this.state.filterObject, `transits.draft_m.gte`) *
                                    this.getDisplay('vessel', 'draft_m').database2display_unit_factor,
                                getKey(this.state.filterObject, `transits.draft_m.lte`) *
                                    this.getDisplay('vessel', 'draft_m').database2display_unit_factor,
                            ]}
                            range={[
                                getKey(this.props.defaultData, `transits.draft_m.gte`),
                                getKey(this.props.defaultData, `transits.draft_m.lte`),
                            ]}
                        ></RangeSlider>
                    </div>
                </div>
                {/* <VesselShortcut
                    value={getKey(this.state.filterObject, 'vessel.name.icontains')}
                    handleVessleNameChange={this.handleVessleNameChange}
                ></VesselShortcut> */}
            </SearchGroup>
        );
    }
}

const VesselFiltersStateToProps = (state: RootState) => ({
    vesselCategories: state.port.vesselCategories,
    defaultData: state.port.defaultData,
    port: state.port.port,
});

const VesselFiltersDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(VesselFiltersStateToProps, VesselFiltersDispatchToProps)(VesselFilters);
